import { useEffect, useState } from 'react';

/** Below this average luminance a row or column counts as a black bar. */
const BLACK = 12;

/** Never trust a scan that claims more than this fraction is bar. */
const MAX_BAR = 0.42;

/** Fallback shape: the standard video frame. */
const DEFAULT_ASPECT = 16 / 9;

/**
 * maxresdefault.jpg does not exist for every upload, and i.ytimg.com answers a
 * 120x90 grey placeholder rather than a 404 when it is missing. Anything this
 * small is that placeholder, not a real frame, so it is rescanned against the
 * fallback thumbnail instead of being measured as a 4:3 card.
 */
const MIN_REAL_THUMB = 320;

const cache = new Map();

/**
 * Works out the aspect ratio of the *picture* inside a YouTube thumbnail.
 *
 * Testimonials arrive filmed however the farmer happened to hold the phone,
 * and some are exported upright into a 16:9 timeline, which bakes black
 * pillarbox bars into the file. YouTube's thumbnail is always 16:9 regardless,
 * so the stored url tells us nothing about how the shot is actually framed.
 *
 * Rather than adding a column to the CMS that somebody has to remember to set
 * for every future upload, this scans the thumbnail for bars and reports the
 * real content shape. Portrait clips get a portrait card, landscape clips get
 * a landscape one, with nothing to configure.
 *
 * Reading pixels needs a CORS-clean canvas. i.ytimg.com serves the header, but
 * if that ever changes the read throws, and every other failure path lands on
 * the same safe default of 16:9 — so the card degrades to an ordinary video
 * thumbnail instead of breaking.
 */
export function useVideoCrop(posterUrl, fallbackUrl) {
  const [aspect, setAspect] = useState(() => cache.get(posterUrl) ?? DEFAULT_ASPECT);

  useEffect(() => {
    if (!posterUrl) return;

    if (cache.has(posterUrl)) {
      setAspect(cache.get(posterUrl));
      return;
    }

    let cancelled = false;

    const done = (value) => {
      cache.set(posterUrl, value);
      if (!cancelled) setAspect(value);
    };

    const scan = (src, allowRetry) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const { naturalWidth: w, naturalHeight: h } = img;
          if (!w || !h) return done(DEFAULT_ASPECT);

          if (w < MIN_REAL_THUMB) {
            // The grey placeholder. hqdefault is always present, so measure that.
            if (allowRetry && fallbackUrl) return scan(fallbackUrl, false);
            return done(DEFAULT_ASPECT);
          }

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0);

          const mean = (data) => {
            let sum = 0;
            for (let i = 0; i < data.length; i += 4) {
              sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
            }
            return sum / (data.length / 4);
          };

          const isDarkCol = (x) => mean(ctx.getImageData(x, 0, 1, h).data) < BLACK;
          const isDarkRow = (y) => mean(ctx.getImageData(0, y, w, 1).data) < BLACK;

          // Walk in from each edge until the picture starts.
          const limitX = Math.floor(w * MAX_BAR);
          const limitY = Math.floor(h * MAX_BAR);

          let left = 0;
          while (left < limitX && isDarkCol(left)) left++;
          let right = w - 1;
          while (right > w - 1 - limitX && isDarkCol(right)) right--;

          let top = 0;
          while (top < limitY && isDarkRow(top)) top++;
          let bottom = h - 1;
          while (bottom > h - 1 - limitY && isDarkRow(bottom)) bottom--;

          const cw = right - left + 1;
          const ch = bottom - top + 1;

          // A scan that collapses to nothing means the heuristic misfired
          // (a genuinely dark shot, say), so keep the default rather than
          // cropping a real frame down to a sliver.
          if (cw < w * 0.15 || ch < h * 0.15) return done(DEFAULT_ASPECT);

          done(cw / ch);
        } catch {
          // Tainted canvas, or no 2d context.
          done(DEFAULT_ASPECT);
        }
      };

      img.onerror = () => {
        if (allowRetry && fallbackUrl) return scan(fallbackUrl, false);
        done(DEFAULT_ASPECT);
      };
      img.src = src;
    };

    scan(posterUrl, true);

    return () => {
      cancelled = true;
    };
  }, [posterUrl, fallbackUrl]);

  return aspect;
}
